import React, { lazy } from 'react';
import { Perm } from '../auth/permissions';

/**
 * Central route registry — single source of truth for:
 *   1. The router's <Route> elements (App.tsx maps over this list).
 *   2. The sidebar's nav items (filtered by user permissions).
 *
 * Adding a route: add an entry here. Don't sprinkle <Route> in App.tsx.
 *
 * `perm: null` = authenticated route, no specific permission required.
 * `perm: Perm.X` = both route gate AND sidebar visibility filter.
 *
 * Each page is loaded lazily so the initial bundle stays small. The
 * sidebar shell loads instantly; module bundles arrive on first nav.
 */

export interface RouteSpec {
  /** URL path (react-router v6 syntax). */
  path: string;
  /** Lazily-imported page component. */
  element: React.LazyExoticComponent<React.ComponentType<any>>;
  /** Required permission, or null for "any authenticated user". */
  perm: string | null;
  /** Sidebar grouping. Omit to hide from sidebar (e.g. detail routes). */
  navGroup?: string;
  /** Sidebar label. Omit to hide from sidebar. */
  navLabel?: string;
}

// ── Lazy page imports — keeps initial bundle small ───────────────────────
const EnterpriseDashboard = lazy(() => import('../pages/EnterpriseDashboard'));
const BlockedEventsCenter = lazy(() => import('../pages/BlockedEventsCenter'));
const RoleBasedPolicies = lazy(() => import('../pages/RoleBasedPolicies'));
const Groups = lazy(() => import('../pages/Groups'));
const Decisions = lazy(() => import('../pages/Decisions'));
const Simulator = lazy(() => import('../pages/Simulator'));
const ComplianceRules = lazy(() => import('../pages/ComplianceRules'));
// Sprint follow-up: replaced the offset-paginated MUI Table with a
// TanStack-Virtual + cursor-paginated module. The old page is intentionally
// removed — keep a single source of truth.
const AgentLogs = lazy(() => import('../features/agent-logs/AgentLogsViewer'));
const RecoveryRequestList = lazy(() => import('../features/recovery/RecoveryRequestList'));
const DeviceUserBinding = lazy(() => import('../pages/DeviceUserBinding'));
const UserManagement = lazy(() => import('../pages/UserManagement'));
const Offenders = lazy(() => import('../pages/Offenders'));
const TamperingEvents = lazy(() => import('../pages/TamperingEvents'));
const UploadBlocking = lazy(() => import('../pages/UploadBlocking'));
const Settings = lazy(() => import('../pages/Settings'));
// Phase 3 — new virtualized audit viewer replaces the legacy AuditLogs
// page. The legacy page stays in src/pages/AuditLogs.tsx for now in
// case anything links to it; the route below points to the new viewer.
const AuditLogs = lazy(() => import('../features/audit/AuditViewer'));

// Phase 1 — Tenant Management feature module.
const TenantList = lazy(() => import('../features/tenants/TenantList'));
const TenantDetail = lazy(() => import('../features/tenants/TenantDetail'));

// Phase 2 — Role Management feature module.
const RoleList = lazy(() => import('../features/roles/RoleList'));
const RoleDetail = lazy(() => import('../features/roles/RoleDetail'));

// Phase 4 — Platform observability (Schema Health + Migration Status).
const SchemaHealth = lazy(() => import('../features/platform/SchemaHealth'));
const MigrationStatus = lazy(() => import('../features/platform/MigrationStatus'));

// Phase 5 — Agent Management refactor (replaces pages/Agents.tsx + AgentDetail.tsx).
const AgentList = lazy(() => import('../features/agents/AgentList'));
const AgentDetail = lazy(() => import('../features/agents/AgentDetail'));

// Phase 6 — Policy Management refactor (replaces pages/Policies.tsx).
const PolicyList = lazy(() => import('../features/policies/PolicyList'));
const PolicyDetail = lazy(() => import('../features/policies/PolicyDetail'));
// Apps rejecting the agent's certificate, with app-scoped pass-through rules.
const TLSRejections = lazy(() => import('../features/pass-through/TLSRejections'));

// Phase 7 — DLP Rules cross-policy view + pattern tester.
const DLPRules = lazy(() => import('../features/dlp/DLPRules'));

// Admin agent installer management — lists published TUF versions per tenant.
const AgentVersions = lazy(() => import('../pages/AgentVersions'));
const AgentPublish = lazy(() => import('../pages/AgentPublish'));

// End-user installer download page (existed in pages/Downloads.tsx but was
// not registered in the route table). Pulls manifest from /api/agents/downloads.
const Downloads = lazy(() => import('../pages/Downloads'));
const DLPUserManual = lazy(() => import('../pages/DLPUserManual'));

// ── Route specs ─────────────────────────────────────────────────────────
//
// Permission assignments are CONSERVATIVE — preserve current behavior
// where existing pages were unguarded. As modules are refactored in
// Phases 1-7, tighten the perm requirements per spec.

export const routes: RouteSpec[] = [
  { path: '/dashboard',      element: EnterpriseDashboard, perm: null,             navGroup: 'Operations', navLabel: 'Dashboard' },
  { path: '/blocked-events', element: BlockedEventsCenter, perm: null,             navGroup: 'Operations', navLabel: 'Blocked Events' },
  { path: '/decisions',      element: Decisions,           perm: Perm.DecisionRead /* hidden from nav */ },

  { path: '/policies',          element: PolicyList,        perm: Perm.PolicyRead, navGroup: 'Security', navLabel: 'Policies' },
  { path: '/policies/:id',      element: PolicyDetail,      perm: Perm.PolicyRead /* hidden from nav */ },
  // policy.write: the list is GET /api/admin/tls-rejections, gated as rule changes are.
  { path: '/policies/tls-rejections', element: TLSRejections, perm: Perm.PolicyWrite, navGroup: 'Security', navLabel: 'Certificate Rejections' },
  { path: '/role-based-policies', element: RoleBasedPolicies, perm: Perm.PolicyRead /* hidden from nav */ },
  { path: '/simulator',         element: Simulator,         perm: Perm.PolicyRead /* hidden from nav */ },
  { path: '/dlp/rules',         element: DLPRules,          perm: Perm.PolicyRead, navGroup: 'Security', navLabel: 'DLP Rules' },
  { path: '/upload-blocking',   element: UploadBlocking,    perm: null,            navGroup: 'Security', navLabel: 'Upload Blocking' },
  { path: '/tampering',         element: TamperingEvents,   perm: null,            navGroup: 'Security', navLabel: 'Tampering Events' },
  { path: '/compliance',        element: ComplianceRules,   perm: null /* hidden from nav */ },

  { path: '/agents',         element: AgentList,        perm: null,             navGroup: 'Devices', navLabel: 'Agents' },
  { path: '/agents/:id',     element: AgentDetail,      perm: null,             /* hidden from nav */ },
  { path: '/agent-logs',     element: AgentLogs,        perm: null,             navGroup: 'Devices', navLabel: 'Agent Logs' },
  { path: '/device-binding', element: DeviceUserBinding, perm: null,            navGroup: 'Devices', navLabel: 'Device Binding' },
  { path: '/devices/recovery', element: RecoveryRequestList, perm: Perm.SettingsManage /* hidden from nav */ },
  { path: '/downloads',      element: Downloads,        perm: null,             navGroup: 'Devices', navLabel: 'Download Agent' },

  { path: '/groups',    element: Groups,         perm: Perm.GroupRead, navGroup: 'Admin', navLabel: 'Groups' },
  { path: '/users',     element: UserManagement, perm: Perm.UserRead,  navGroup: 'Admin', navLabel: 'Users' },
  { path: '/offenders', element: Offenders,      perm: null,           /* hidden from nav */ },
  { path: '/audit',     element: AuditLogs,      perm: Perm.AuditRead, navGroup: 'Admin', navLabel: 'Audit Logs' },

  { path: '/settings', element: Settings, perm: Perm.SettingsManage, navGroup: 'System', navLabel: 'Settings' },
  { path: '/user-manual', element: DLPUserManual, perm: null, navGroup: 'Help', navLabel: 'User Manual' },

  // ── Admin: Agent installer versions per tenant ─────────────────────────
  { path: '/admin/agents/versions', element: AgentVersions, perm: Perm.SettingsManage, navGroup: 'Admin', navLabel: 'Agent Versions' },
  // Releases come from the signing pipeline now; this page only says so, for old links.
  { path: '/admin/agents/publish',  element: AgentPublish,  perm: Perm.SettingsManage /* hidden from nav */ },

  // ── Platform (platform-staff only — tenant.manage) ─────────────────────
  { path: '/platform/tenants',        element: TenantList,   perm: Perm.TenantManage, navGroup: 'Platform', navLabel: 'Tenants' },
  { path: '/platform/tenants/:slug',  element: TenantDetail, perm: Perm.TenantManage /* hidden from nav */ },

  // ── Role Management (admin only — role.manage) ─────────────────────────
  { path: '/admin/roles',     element: RoleList,   perm: Perm.RoleManage, navGroup: 'Admin', navLabel: 'Roles' },
  { path: '/admin/roles/:id', element: RoleDetail, perm: Perm.RoleManage /* hidden from nav */ },

  // ── Platform observability (Phase 4) ───────────────────────────────────
  { path: '/platform/schema-health', element: SchemaHealth,    perm: Perm.TenantManage, navGroup: 'Platform', navLabel: 'Schema Health' },
  { path: '/platform/migrations',    element: MigrationStatus, perm: Perm.TenantManage, navGroup: 'Platform', navLabel: 'Migrations' },
];

/** Order in which sidebar groups render. Groups not listed here go last alphabetically. */
export const navGroupOrder = ['Operations', 'Security', 'Devices', 'Admin', 'Platform', 'System', 'Help'];
